import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Shield, TrendingUp, MapPin, Clock, CheckCircle2, Calculator, Users, DollarSign, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Images
const NYC_IMG = "https://images.unsplash.com/photo-1757621448471-cf5fcd2b84a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtYW5oYXR0YW4lMjBza3lsaW5lJTIwYXBhcnRtZW50JTIwYnVpbGRpbmclMjBnbGFzcyUyMHRvd2VyfGVufDB8fHx8MTc3MzA5NTk4Nnww&ixlib=rb-4.1.0&q=85";
const MIAMI_IMG = "https://images.unsplash.com/photo-1740484408205-7f23daabbfdd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxtaWFtaSUyMHdhdGVyZnJvbnQlMjBjb25kbyUyMHRvd2VycyUyMHN1bm55JTIwZGF5JTIwYmx1ZSUyMHNreXxlbnwwfHx8fDE3NzMwOTU5OTZ8MA&ixlib=rb-4.1.0&q=85";
const INTERIOR_IMG = "https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodCUyMHdoaXRlJTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3NzMwOTYwMDF8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success("Successfully subscribed!");
      setEmail("");
    } catch (err) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Full Width with Stats */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={NYC_IMG} alt="NYC luxury condo" className="w-full h-full object-cover" style={{ transform: 'rotate(0.4deg) scale(1.01)' }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A2E]/90 via-[#1A1A2E]/70 to-[#1A1A2E]/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-16 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-3 py-1.5 bg-[#0066FF] text-white text-xs font-bold uppercase tracking-wider rounded mb-4">
                1031 Exchange Specialists
              </span>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5" data-testid="hero-title">
                Consider Condo Units for Your 1031 Exchange
              </h1>
              
              <p className="text-lg text-white/80 leading-relaxed mb-6 max-w-lg">
                Browse available condo units in NYC and Miami for your 1031 exchange.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <Link to="/properties">
                  <Button className="btn-primary px-6 py-5 text-base rounded-lg group font-semibold" data-testid="hero-view-properties-btn">
                    View Properties
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/calculator">
                  <Button variant="outline" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 px-6 py-5 text-base rounded-lg font-semibold">
                    <Calculator className="mr-2 w-5 h-5" />
                    Calculate Savings
                  </Button>
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 text-white/70 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Available Listings</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span>Exchange Ready</span>
                </div>
              </div>
            </motion.div>

            {/* Stats Card */}
            <motion.div 
              className="hidden lg:block"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: "stat-volume", value: "$50B+", label: "Annual 1031 Volume", icon: DollarSign },
                    { id: "stat-deadline", value: "45", label: "Day ID Deadline", icon: Clock },
                    { id: "stat-deferral", value: "100%", label: "Tax Deferral", icon: Shield },
                    { id: "stat-markets", value: "2", label: "Prime Markets", icon: MapPin },
                  ].map((stat) => (
                    <div key={stat.id} className="bg-white/10 rounded-xl p-4 text-center">
                      <stat.icon className="w-5 h-5 text-[#60A5FA] mx-auto mb-2" />
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-xs text-white/60">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Value Props - Compact Bar */}
      <section className="bg-gray-900 py-4">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-2 text-sm">
            {[
              { id: "vp-assets", text: "Institutional-Quality Properties" },
              { id: "vp-markets", text: "NYC & Miami Markets" },
              { id: "vp-deadline", text: "Meet 45-Day Deadline" },
              { id: "vp-ownership", text: "Simplified Ownership" },
            ].map((item) => (
              <div key={item.id} className="flex items-center gap-2 text-gray-300">
                <CheckCircle2 className="w-4 h-4 text-[#0066FF]" />
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Entry Funnels - Where Are You In Your Journey? */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-wider">Get Started</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mt-2 mb-4">
              Where Are You In Your 1031 Journey?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Select your current situation and we'll guide you to the right resources.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Funnel 1: I Just Sold a Property */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
            >
              <Link to="/properties" className="block h-full">
                <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 h-full hover:border-[#0066FF] hover:shadow-xl transition-all duration-300 group cursor-pointer">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-green-500 transition-colors">
                    <DollarSign className="w-7 h-7 text-green-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A2E] mb-3">
                    I Just Sold a Property
                  </h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                    Your 45-day clock is ticking. Browse available listings and identify your replacement properties today.
                  </p>
                  <div className="flex items-center text-[#0066FF] font-semibold text-sm group-hover:gap-3 transition-all">
                    <span>Browse Available Properties</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Funnel 2: I'm Within 45 Days */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Link to="/contact" className="block h-full">
                <div className="bg-white rounded-2xl border-2 border-red-100 p-8 h-full hover:border-red-400 hover:shadow-xl transition-all duration-300 group cursor-pointer relative overflow-hidden">
                  <div className="absolute top-4 right-4">
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full animate-pulse">
                      URGENT
                    </span>
                  </div>
                  <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-red-500 transition-colors">
                    <Clock className="w-7 h-7 text-red-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A2E] mb-3">
                    I'm Within 45 Days
                  </h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                    Time is critical. Let our specialists help you identify qualifying properties before your deadline expires.
                  </p>
                  <div className="flex items-center text-red-500 font-semibold text-sm group-hover:gap-3 transition-all">
                    <span>Get Immediate Assistance</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Funnel 3: I'm Planning a 1031 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/guide" className="block h-full">
                <div className="bg-white rounded-2xl border-2 border-gray-100 p-8 h-full hover:border-[#0066FF] hover:shadow-xl transition-all duration-300 group cursor-pointer">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#0066FF] transition-colors">
                    <TrendingUp className="w-7 h-7 text-[#0066FF] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A2E] mb-3">
                    I'm Planning a 1031
                  </h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                    Get ahead of the timeline. Learn the process, explore markets, and estimate your potential tax deferral.
                  </p>
                  <div className="flex items-center text-[#0066FF] font-semibold text-sm group-hover:gap-3 transition-all">
                    <span>Start Learning</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>

          {/* Not Sure? CTA */}
          <div className="text-center mt-10">
            <p className="text-gray-500 mb-3">Not sure where to start?</p>
            <Link to="/contact">
              <Button variant="outline" className="rounded-lg">
                <Users className="w-4 h-4 mr-2" />
                Talk to a 1031 Specialist
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why 1031 + Condos Combined Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left - Why 1031Condo */}
            <div>
              <span className="text-[#0066FF] text-sm font-bold uppercase tracking-wider">Why Choose Us</span>
              <h2 className="text-3xl font-bold text-[#1A1A2E] mt-2 mb-4">
                Your 1031 Exchange Partner
              </h2>
              <p className="text-gray-600 mb-6">
                1031Condo.com helps clients identify and acquire individual condo units suitable for 1031 exchanges through licensed real estate brokerage services.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Clock, title: "Streamlined Search", desc: "Access relevant listings for your 45-day identification" },
                  { icon: Building2, title: "Buyer Representation", desc: "We act as your buyer's agent, presenting offers and negotiating on your behalf" },
                  { icon: Shield, title: "Transaction Coordination", desc: "Guidance through identification, negotiation, and closing" },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 p-4 bg-gray-50 rounded-lg hover:bg-blue-50 transition-colors">
                    <div className="w-10 h-10 bg-[#0066FF]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-[#0066FF]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1A1A2E]">{item.title}</h3>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Why Condos */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <span className="text-[#0066FF] text-sm font-bold uppercase tracking-wider">Property Overview</span>
              <h2 className="text-3xl font-bold text-[#1A1A2E] mt-2 mb-4">
                Why Condo Units?
              </h2>
              <p className="text-gray-600 mb-6">
                Many clients completing 1031 exchanges seek to simplify ownership while maintaining direct real estate holdings in prime markets. Individual condo units offer a streamlined alternative to managing entire buildings.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  { num: "01", title: "Simplified Ownership", desc: "No building management headaches" },
                  { num: "02", title: "Lower Complexity", desc: "HOA handles maintenance" },
                  { num: "03", title: "Premium Quality", desc: "High-end amenities included" },
                  { num: "04", title: "Strong Liquidity", desc: "Broader resale market" },
                ].map((item) => (
                  <div key={item.num} className="bg-white rounded-lg p-4 border border-gray-100">
                    <span className="text-2xl font-bold text-[#0066FF]/20">{item.num}</span>
                    <h4 className="font-semibold text-[#1A1A2E] text-sm mt-1">{item.title}</h4>
                    <p className="text-xs text-gray-500 mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Markets - Side by Side Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <span className="text-[#0066FF] text-sm font-bold uppercase tracking-wider">Our Markets</span>
            <h2 className="text-3xl font-bold text-[#1A1A2E] mt-2">
              Global Gateway Cities
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* NYC */}
            <motion.div
              className="group relative overflow-hidden rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={NYC_IMG} alt="NYC" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white mb-2">New York City</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["#1 Financial Center", "Large Buyer Pool", "Limited Supply"].map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-white/20 backdrop-blur text-white text-xs rounded">{tag}</span>
                  ))}
                </div>
                <Link to="/nyc">
                  <Button className="bg-white text-[#1A1A2E] hover:bg-gray-100 rounded-lg font-semibold" data-testid="market-nyc-btn">
                    Explore NYC <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Miami */}
            <motion.div
              className="group relative overflow-hidden rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img src={MIAMI_IMG} alt="Miami" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white mb-2">Miami</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {["Fastest Growing", "No State Tax", "International Market"].map((tag) => (
                    <span key={tag} className="px-2 py-1 bg-white/20 backdrop-blur text-white text-xs rounded">{tag}</span>
                  ))}
                </div>
                <Link to="/miami">
                  <Button className="bg-white text-[#1A1A2E] hover:bg-gray-100 rounded-lg font-semibold" data-testid="market-miami-btn">
                    Explore Miami <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process + Calculator CTA Combined */}
      <section className="py-16 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-8 items-center">
            {/* Process Steps */}
            <div className="lg:col-span-3">
              <span className="text-[#60A5FA] text-sm font-bold uppercase tracking-wider">How It Works</span>
              <h2 className="text-3xl font-bold text-white mt-2 mb-8">Three Simple Steps</h2>
              
              <div className="flex flex-col md:flex-row gap-4">
                {[
                  { num: "1", title: "Sell Property", desc: "Work with a qualified intermediary" },
                  { num: "2", title: "Identify (45 Days)", desc: "Select from our available listings" },
                  { num: "3", title: "Close (180 Days)", desc: "Complete your tax-deferred exchange" },
                ].map((step) => (
                  <div key={step.num} className="flex-1 bg-white/5 rounded-xl p-5 border border-white/10">
                    <div className="w-8 h-8 bg-[#0066FF] rounded-full flex items-center justify-center text-white font-bold text-sm mb-3">
                      {step.num}
                    </div>
                    <h3 className="text-white font-semibold mb-1">{step.title}</h3>
                    <p className="text-gray-400 text-sm">{step.desc}</p>
                  </div>
                ))}
              </div>
              
              <Link to="/process" className="inline-flex items-center gap-2 text-[#60A5FA] mt-6 hover:underline text-sm font-medium">
                Learn more about the process <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Calculator CTA */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-[#0066FF] to-blue-600 rounded-2xl p-6 text-center">
                <Calculator className="w-12 h-12 text-white/80 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Calculate Your Savings</h3>
                <p className="text-blue-100 mb-4 text-sm">See how much you could defer with a 1031 exchange</p>
                <Link to="/calculator">
                  <Button className="bg-white text-[#0066FF] hover:bg-blue-50 w-full py-5 rounded-lg font-bold" data-testid="calculator-cta-btn">
                    Try Calculator <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture - Compact */}
      <section className="py-12 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-[#1A1A2E] mb-2">Stay Informed on New Listings</h2>
              <p className="text-gray-600">Be first to know when new properties become available.</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-3 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 px-4 rounded-lg border-2 border-gray-200 focus:border-[#0066FF] min-w-[250px]"
                data-testid="subscribe-email-input"
              />
              <Button type="submit" className="btn-primary h-12 px-6 rounded-lg font-semibold whitespace-nowrap" disabled={loading} data-testid="subscribe-submit-btn">
                {loading ? "..." : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Social Proof / Trust */}
      <section className="py-10 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>500+ Clients Served</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              <span>$2B+ Properties Exchanged</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5" />
              <span>Operated by Krieger Real Estate</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
